import { useEffect, useState } from 'react';
import { useMultiAgentStore, AgentRole, AgentStatus } from '../stores/multiAgentStore';
import { useAuthStore } from '../stores/authStore';

const agentConfig = {
  architect: {
    name: 'Architect',
    description: 'Analyzes requirements and creates architecture plans',
    color: 'blue',
    icon: '🏗️',
  },
  developer: {
    name: 'Developer',
    description: 'Implements code based on the approved plan',
    color: 'green',
    icon: '👨‍💻',
  },
  qa: {
    name: 'QA Engineer',
    description: 'Reviews implementation and ensures quality',
    color: 'purple',
    icon: '🔍',
  },
};

const statusConfig: Record<AgentStatus, { label: string; color: string }> = {
  idle: { label: 'Idle', color: 'gray' },
  thinking: { label: 'Thinking', color: 'yellow' },
  working: { label: 'Working', color: 'blue' },
  reviewing: { label: 'Reviewing', color: 'purple' },
  completed: { label: 'Completed', color: 'green' },
  error: { label: 'Error', color: 'red' },
};

export default function MultiAgentPage() {
  const { user } = useAuthStore();
  const {
    sessions,
    currentSession,
    loading,
    error,
    fetchSessions,
    fetchSession,
    createSession,
    runArchitectPhase,
    approvePlan,
    runDeveloperPhase,
    runQAPhase,
    approveFinal,
    rejectSession,
    cancelSession,
    deleteSession,
    clearCurrentSession,
    clearError,
  } = useMultiAgentStore();

  const [newRequest, setNewRequest] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleCreateSession = async () => {
    if (!newRequest.trim()) return;
    const session = await createSession(newRequest, 'default-vfs');
    if (session) {
      setNewRequest('');
      // Auto-run architect phase
      await runArchitectPhase(session.id);
    }
  };

  const handleApprovePlan = async () => {
    if (!currentSession) return;
    await approvePlan(currentSession.id);
    await runDeveloperPhase(currentSession.id);
  };

  const handleApproveFinal = async () => {
    if (!currentSession) return;
    await approveFinal(currentSession.id);
  };

  const handleReject = async () => {
    if (!currentSession || !rejectReason.trim()) return;
    await rejectSession(currentSession.id, rejectReason);
    setShowRejectModal(false);
    setRejectReason('');
  };

  const getStatusBadge = (status: AgentStatus) => {
    const config = statusConfig[status];
    const colorClasses: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClasses[config.color]}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Multi-Agent Workflow</h1>
          <p className="text-gray-600 mt-2">
            Collaborate with three AI agents: Architect, Developer, and QA Engineer
          </p>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex justify-between items-center">
              <p className="text-red-700">{error}</p>
              <button onClick={clearError} className="text-red-500 hover:text-red-700">
                ✕
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Session List & New Session */}
          <div className="lg:col-span-1 space-y-6">
            {/* New Session */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">New Multi-Agent Session</h2>
              <textarea
                value={newRequest}
                onChange={(e) => setNewRequest(e.target.value)}
                placeholder="Describe what you want to build or modify..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={4}
              />
              <button
                onClick={handleCreateSession}
                disabled={loading || !newRequest.trim()}
                className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating...' : 'Start Multi-Agent Session'}
              </button>
            </div>

            {/* Session List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Sessions</h2>
              {sessions.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No sessions yet</p>
              ) : (
                <div className="space-y-3">
                  {sessions.filter(Boolean).slice(0, 5).map((session) => (
                    <button
                      key={session.id}
                      onClick={() => fetchSession(session.id)}
                      className={`w-full p-4 rounded-lg border text-left transition-colors ${
                        currentSession?.id === session.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <p className="font-medium text-gray-900 truncate">{session.userRequest}</p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm text-gray-500">
                          {new Date(session.createdAt).toLocaleDateString()}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          session.status === 'completed' ? 'bg-green-100 text-green-800' :
                          session.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                          session.status === 'error' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {session.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Current Session */}
          <div className="lg:col-span-2">
            {!currentSession ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-6xl mb-4">🤖</div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Session</h2>
                <p className="text-gray-500">
                  Create a new multi-agent session to get started with collaborative AI development
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Session Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{currentSession.userRequest}</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Started: {new Date(currentSession.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {currentSession.status !== 'completed' && currentSession.status !== 'cancelled' && (
                        <button
                          onClick={() => cancelSession(currentSession.id)}
                          className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        onClick={() => deleteSession(currentSession.id)}
                        className="px-3 py-1 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{currentSession.currentPhase}</span>
                      <span className="text-gray-500">
                        {currentSession.implementationProgress}% complete
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${currentSession.implementationProgress}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Agent Cards */}
                <div className="grid grid-cols-3 gap-4">
                  {(Object.keys(agentConfig) as AgentRole[]).map((role) => {
                    const agent = currentSession.agents?.[role] || { status: 'idle' as const, taskCount: 0, completedTasks: 0 };
                    const config = agentConfig[role];
                    return (
                      <div
                        key={role}
                        className={`bg-white rounded-xl shadow-sm border p-4 ${
                          agent.status === 'thinking' || agent.status === 'working' || agent.status === 'reviewing'
                            ? 'border-blue-300 ring-2 ring-blue-100'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-2xl">{config.icon}</span>
                          <div>
                            <h3 className="font-semibold text-gray-900">{config.name}</h3>
                            {getStatusBadge(agent.status)}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">{config.description}</p>
                        {agent.taskCount > 0 && (
                          <div className="mt-3 text-xs text-gray-600">
                            {agent.completedTasks}/{agent.taskCount} tasks completed
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Architecture Plan */}
                {currentSession.architecturePlan && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Architecture Plan</h3>
                    
                    <div className="mb-4">
                      <p className="text-gray-700">{currentSession.architecturePlan.overview}</p>
                    </div>

                    {/* Files */}
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">File Changes</h4>
                      <div className="space-y-2">
                        {currentSession.architecturePlan.fileStructure.slice(0, 10).map((file, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              file.action === 'create' ? 'bg-green-100 text-green-800' :
                              file.action === 'modify' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {file.action}
                            </span>
                            <span className="text-sm font-mono text-gray-700">{file.path}</span>
                            <span className="text-xs text-gray-500">{file.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Risks */}
                    {currentSession.architecturePlan.risks.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">⚠️ Risk Assessment</h4>
                        <div className="space-y-2">
                          {currentSession.architecturePlan.risks.map((risk, i) => (
                            <div key={i} className="p-2 bg-yellow-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  risk.level === 'high' ? 'bg-red-100 text-red-800' :
                                  risk.level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {risk.level}
                                </span>
                                <span className="text-sm text-gray-700">{risk.description}</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Mitigation: {risk.mitigation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Approval Actions */}
                    {currentSession.status === 'awaiting_plan_approval' && (
                      <div className="flex gap-3 pt-4 border-t">
                        <button
                          onClick={handleApprovePlan}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          Approve Plan & Start Implementation
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSessionId(currentSession.id);
                            setShowRejectModal(true);
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Implementation Results */}
                {(currentSession.filesModified > 0 || currentSession.filesCreated > 0) && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">📁 Implementation Results</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600">{currentSession.filesCreated}</div>
                        <div className="text-sm text-green-700">Files Created</div>
                      </div>
                      <div className="p-4 bg-yellow-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-yellow-600">{currentSession.filesModified}</div>
                        <div className="text-sm text-yellow-700">Files Modified</div>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-600">{currentSession.filesDeleted}</div>
                        <div className="text-sm text-red-700">Files Deleted</div>
                      </div>
                    </div>

                    {currentSession.status === 'awaiting_final_approval' && (
                      <div className="flex gap-3 mt-4 pt-4 border-t">
                        <button
                          onClick={handleApproveFinal}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          Approve & Finalize
                        </button>
                        <button
                          onClick={() => setShowRejectModal(true)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Reject Changes
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Messages */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">💬 Agent Messages</h3>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {currentSession.messages.map((message) => {
                      const config = agentConfig[message.agent as AgentRole] || agentConfig.architect;
                      return (
                        <div key={message.id} className="flex gap-3">
                          <span className="text-xl">{config.icon}</span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{message.agentName}</span>
                              <span className="text-xs text-gray-500">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-gray-700 mt-1 whitespace-pre-wrap">{message.content}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Completion Summary */}
                {currentSession.status === 'completed' && currentSession.summary && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">✅ Session Complete</h3>
                    <p className="text-green-700">{currentSession.summary}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Session</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
